import { createMemo } from 'solid-js';
import { transactions, type Transaction } from '~/stores/transactions-store';

// What is held, walked out of the blotter in order.
//
// While every row was a buy a position was a sum, and portfolio-store added it
// up inline. A sell ends that. Units subtract, but book cost does not subtract
// the proceeds — it relieves the *cost* of the units sold, and the difference
// between those two numbers is the realised gain. So the running average cost
// has to be carried forward trade by trade, and that is a walk, not a sum.
//
// Average cost, which is what Canada uses: the ACB of a security is the average
// across every unit held, and a disposal relieves that average. Not FIFO, not
// specific-lot — there is no lot to pick, so nothing here records one, and
// selling half a position always relieves exactly half its base whatever order
// the units were bought in.
//
// One walk, here, shared by everything downstream. The alternative was every
// consumer doing its own subtraction, which is how the units on one screen come
// to disagree with the units on another.

/** Below this, a position has been sold out and the remainder is float noise. */
const DUST = 1e-9;

export interface Position {
    accountId: string;
    symbol: string;
    units: number;
    /** What the units still held cost, commissions included. */
    bookCost: number;
    /**
     * The part of that cost each loan paid for, keyed by loan id.
     *
     * Carried through disposals pro rata rather than recomputed from the buys:
     * sell half a position and half of whatever each loan put into it has been
     * disposed of too. Summing the loan-funded buys instead would report a
     * liquidated account as still fully borrowed.
     */
    loanCost: Map<string, number>;
}

export interface Disposal {
    transaction: Transaction;
    /** Cost base the sale relieved. */
    relieved: number;
    /** Units × price, less the fee — the cash the sale actually produced. */
    proceeds: number;
    /** Proceeds less the cost relieved. */
    gain: number;
    /** Units the sale could not find, because more was sold than was held. */
    unmatched: number;
}

/** Units × price, plus the fee on a buy and less it on a sell. Both directions
    move against you, which is why the fee is stored unsigned. */
export const proceedsOf = (t: Transaction): number => t.units * t.price - t.fee;
export const costOf = (t: Transaction): number => t.units * t.price + t.fee;

/** What the trade did to the account's cash. A loan-funded buy was paid by the
    lender and never touched it; the proceeds of selling that same position do
    land here, because the broker credits the account whether or not the loan is
    then paid down. */
export const cashFlow = (t: Transaction): number => (t.kind === 'sell' ? proceedsOf(t) : t.loanId == null ? -costOf(t) : 0);

export const key = (accountId: string, symbol: string): string => `${accountId} ${symbol}`;

/** Oldest first, which is the only order an average cost can be carried in.
    `created_at` then id break a same-day tie the same way the blotter does, so
    two trades entered on one date walk in the order they were recorded. */
export const ordered = (rows: readonly Transaction[]): Transaction[] =>
    [...rows].sort((a, b) => a.date.localeCompare(b.date) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || a.id.localeCompare(b.id));

/**
 * The walk itself, over whatever rows it is handed.
 *
 * A plain function rather than only a memo so it can be run against a blotter
 * that is not the live one — which is what scripts/check-sells.ts does, and the
 * only way to check an average cost against a worked example without signing in
 * and typing trades.
 */
export const walkBlotter = (rows: readonly Transaction[]): { positions: Map<string, Position>; disposals: Disposal[] } => {
    const positions = new Map<string, Position>();
    const disposals: Disposal[] = [];

    for (const t of ordered(rows)) {
        const at = key(t.accountId, t.symbol);
        const held = positions.get(at) ?? { accountId: t.accountId, symbol: t.symbol, units: 0, bookCost: 0, loanCost: new Map<string, number>() };
        positions.set(at, held);

        if (t.kind === 'buy') {
            const cost = costOf(t);
            held.units += t.units;
            held.bookCost += cost;
            if (t.loanId != null) held.loanCost.set(t.loanId, (held.loanCost.get(t.loanId) ?? 0) + cost);
            continue;
        }

        // Clamped rather than allowed to go short. Selling more than you hold is
        // a typo or a missing buy, and either way a negative position would
        // propagate a nonsense cost base through every figure downstream. The
        // overage is reported instead.
        const sold = Math.min(t.units, held.units);
        const share = held.units > 0 ? sold / held.units : 0;
        const relieved = held.bookCost * share;

        held.units -= sold;
        held.bookCost -= relieved;
        for (const [loanId, amount] of held.loanCost) held.loanCost.set(loanId, amount * (1 - share));

        const proceeds = proceedsOf(t);
        disposals.push({ transaction: t, relieved, proceeds, gain: proceeds - relieved, unmatched: t.units - sold });
    }

    // A position sold out over several partial sales lands on 1e-13 units rather
    // than on zero, and that renders as a holding with a cost base of a
    // thousandth of a cent. Snapped once here so no caller has to know.
    for (const held of positions.values()) {
        if (held.units >= DUST) continue;
        held.units = 0;
        held.bookCost = 0;
        held.loanCost.clear();
    }

    return { positions, disposals };
};

const walk = createMemo(() => walkBlotter(transactions));

export const positions = createMemo(() => walk().positions);

/** Every sale, oldest first — the realised side of the book. */
export const disposals = createMemo(() => walk().disposals);

/** Still held, sold-out positions dropped rather than shown at zero. */
export const positionsIn = (accountId: string): Position[] =>
    [...positions().values()].filter((p) => p.accountId === accountId && p.units > 0).sort((a, b) => a.symbol.localeCompare(b.symbol));

export const positionAt = (accountId: string, symbol: string): Position | null => positions().get(key(accountId, symbol)) ?? null;

/** Realised gains, in dollars. Scoped to an account, a position, or the book. */
export const realised = (where?: { accountId?: string; symbol?: string }): number =>
    disposals().reduce((sum, d) => {
        if (where?.accountId != null && d.transaction.accountId !== where.accountId) return sum;
        if (where?.symbol != null && d.transaction.symbol !== where.symbol) return sum;
        return sum + d.gain;
    }, 0);

/**
 * Units held on a date, for a position.
 *
 * Strictly-before, because that is what the one caller needs: entitlement to a
 * distribution is decided by the units held before the ex-date, and buying on
 * the ex-date is buying without it. Selling on the ex-date likewise sells with
 * it — the seller is on the register the day before and gets paid.
 */
export const unitsBefore = (rows: readonly Transaction[], date: string): number =>
    // Clamped for the same reason the walk is: an over-sell would otherwise pay
    // a negative distribution.
    Math.max(0, rows.reduce((sum, t) => (t.date >= date ? sum : t.kind === 'sell' ? sum - t.units : sum + t.units), 0));
